// ===========================================
// CHANNEL ECONOMICS — what each sales channel earns and costs
// ===========================================
// Per sales category: revenue share, gross margin %, delivery as a share of
// the channel's revenue, and deduction incidence (deductions attributed by
// each customer's category). One line of computed commentary renders under
// the table ONLY when the numbers themselves say it — template sentences
// with real figures, never speculation.

import clsx from 'clsx';
import { fmtMoneyShort, CATEGORY_CHIP } from '../../utils/cpg';
import type { PnlAnalysis } from '../../lib/pnlAnalysis';

const Dash = () => <span className="text-slate-300">—</span>;
const pct1 = (n: number) => `${(Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '')}%`;
// Same margin bands ProductsPage and the build-down use.
const marginTone = (pct: number) => (pct < 20 ? 'text-red-600' : pct < 40 ? 'text-amber-600' : 'text-emerald-700');

export default function ChannelEconomics({ a, hasCosts }: { a: PnlAnalysis; hasCosts: boolean }) {
  if (a.channels.length === 0) return null;
  return (
    <div className="card p-0 overflow-x-auto">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-lg font-semibold text-slate-900">Channel economics</h2>
        <p className="text-sm text-slate-500">Where the revenue comes from — and what each channel gives back to margin, delivery and deductions.</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
            <th className="px-4 py-2 font-medium text-left">Channel</th>
            <th className="px-4 py-2 font-medium text-right">Revenue</th>
            <th className="px-4 py-2 font-medium text-right">Share</th>
            <th className="px-4 py-2 font-medium text-right">Margin %</th>
            <th className="px-4 py-2 font-medium text-right">Delivery</th>
            <th className="px-4 py-2 font-medium text-right">Deductions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {a.channels.map((c) => (
            <tr key={c.category}>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <span className={clsx('px-1.5 py-0.5 rounded text-xs font-medium', CATEGORY_CHIP[c.category] || 'bg-slate-100 text-slate-600')}>{c.label}</span>
                <span className="block text-[11px] text-slate-400 mt-0.5 pl-0.5">{c.accounts} account{c.accounts === 1 ? '' : 's'} · {c.orders} order{c.orders === 1 ? '' : 's'}</span>
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900 whitespace-nowrap">{fmtMoneyShort(c.revenueCents)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{c.sharePct}%</td>
              <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                {c.gmPct != null ? <span className={clsx('font-medium', marginTone(c.gmPct))}>{c.gmPct}%</span> : <Dash />}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
                {c.deliveryCents > 0 ? (
                  <>
                    {fmtMoneyShort(c.deliveryCents)}
                    <span className="block text-[11px] text-slate-400">
                      {c.deliveryPct != null ? pct1(c.deliveryPct) : ''}{c.drops ? ` · ${c.drops} drops` : ''}
                    </span>
                  </>
                ) : <Dash />}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
                {c.dedCents != null && c.dedCents > 0 ? (
                  <>
                    {fmtMoneyShort(c.dedCents)}
                    {c.dedPct != null && <span className="block text-[11px] text-slate-400">{pct1(c.dedPct)} of channel revenue</span>}
                  </>
                ) : <Dash />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {a.commentary.length > 0 && (
        <div className="px-4 py-2.5 border-t border-slate-100 space-y-1">
          {a.commentary.map((line, i) => (
            <p key={i} className="text-xs text-slate-600">{line}</p>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 px-4 py-2.5 border-t border-slate-50">
        Computed from counted wholesale orders, split by each customer’s sales category; deductions attribute to the
        deducting customer’s channel.
        {!hasCosts && ' Margin % fills in once you enter cost per case on Wholesale → Channels — nothing is estimated.'}
        {a.dedMixedCurrency && ' Deductions span more than one currency, so per-channel deduction figures are omitted rather than blended.'}
      </p>
    </div>
  );
}
